import { onMounted, ref } from 'vue';

type Appearance = 'light' | 'dark' | 'system';

export function updateTheme(value: Appearance) {
    if (typeof window === 'undefined') {
        return;
    }

    if (value === 'system') {
        const mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
        const systemTheme = mediaQueryList.matches ? 'dark' : 'light';

        document.documentElement.classList.toggle('dark', systemTheme === 'dark');
    } else {
        document.documentElement.classList.toggle('dark', value === 'dark');
    }
}

const setCookie = (name: string, value: string, days = 365) => {
    if (typeof document === 'undefined') {
        return;
    }

    const maxAge = days * 24 * 60 * 60;

    document.cookie = `${name}=${value};path=/;max-age=${maxAge};SameSite=Lax`;
};

const mediaQuery = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    return window.matchMedia('(prefers-color-scheme: dark)');
};

const getStoredAppearance = () => {
    if (typeof window === 'undefined') {
        return null;
    }

    return localStorage.getItem('appearance') as Appearance | null;
};

const handleSystemThemeChange = () => {
    const currentAppearance = getStoredAppearance();

    updateTheme(currentAppearance || 'system');
};

export function initializeTheme() {
    if (typeof window === 'undefined') {
        return;
    }

    // Force dark theme always
    updateTheme('dark');
}

export function useAppearance() {
    // Force dark theme always
    const appearance = ref<Appearance>('dark');

    onMounted(() => {
        // Always set to dark, ignore any saved preference
        appearance.value = 'dark';
    });

    function updateAppearance(value: Appearance) {
        // Ignore the value and always keep dark theme
        appearance.value = 'dark';

        // Store dark in localStorage
        localStorage.setItem('appearance', 'dark');

        // Store dark in cookie for SSR
        setCookie('appearance', 'dark');

        updateTheme('dark');
    }

    return {
        appearance,
        updateAppearance,
    };
}
